import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, File, CheckCircle, AlertCircle, Loader2, FolderOpen, Image as ImageIcon, Music, Film, Package, Trash2, RefreshCw, ExternalLink, Download, FileJson } from 'lucide-react';
import { supabase } from '../../supabase/client';
import { decode } from 'base64-arraybuffer';

// JSON 映射类型
interface AssetMapping {
  [assetId: string]: {
    name: string;
    category: 'sprites' | 'costumes' | 'backdrops' | 'sounds';
  };
}

interface UploadedFile {
  id: string;
  name: string;
  path: string;
  size: number;
  type: string;
  url: string;
  uploadedAt: string;
  category: string;
}

// 上传队列中的单个任务
interface QueueItem {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error' | 'retrying';
  progress: number;
  message: string;
  category: string;
  retryCount: number;
  error?: string;
}

// 上传队列整体状态
interface QueueState {
  isRunning: boolean;
  totalFiles: number;
  successCount: number;
  failCount: number;
  currentBatch: number;
  totalBatches: number;
  isPaused: boolean;
  isComplete: boolean;
}

const ASSET_CATEGORIES = [
  { id: 'sprites', label: '角色 (Sprites)', icon: Package, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  { id: 'sounds', label: '声音 (Sounds)', icon: Music, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  { id: 'backdrops', label: '背景 (Backdrops)', icon: Film, color: 'text-green-600', bgColor: 'bg-green-50' },
  { id: 'costumes', label: '造型 (Costumes)', icon: ImageIcon, color: 'text-orange-600', bgColor: 'bg-orange-50' },
];

const BATCH_SIZE = 20;
const MAX_RETRIES = 3;
const BATCH_DELAY_MS = 1000;

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function ScratchAssetsUpload() {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [assetMapping, setAssetMapping] = useState<AssetMapping>({});
  const [jsonUploaded, setJsonUploaded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isBatchDeleting, setIsBatchDeleting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, message: '' });

  // 上传队列相关状态
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [queueState, setQueueState] = useState<QueueState>({
    isRunning: false, totalFiles: 0, successCount: 0, failCount: 0,
    currentBatch: 0, totalBatches: 0, isPaused: false, isComplete: false,
  });
  const [isDragging, setIsDragging] = useState(false);

  // 分类整理相关状态
  const [isOrganizing, setIsOrganizing] = useState(false);
  const [organizeProgress, setOrganizeProgress] = useState({ current: 0, total: 0, message: '' });
  const [organizeResult, setOrganizeResult] = useState<{ moved: number; skipped: number; failed: number } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);
  const queueAbortRef = useRef(false);

  const getCategoryByAssetId = (fileName: string): string => {
    const assetId = fileName.replace(/\.[^/.]+$/, '');
    const mapping = assetMapping[assetId];
    return mapping ? mapping.category : 'sprites';
  };

  const loadUploadedFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      // Supabase Storage list 单次最多返回 1000 条，需要分页获取全部
      const allRawFiles: any[] = [];
      const PAGE_SIZE = 1000;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase.storage.from('scratch-assets').list('assets', {
          limit: PAGE_SIZE,
          offset,
          sortBy: { column: 'name', order: 'asc' },
        });
        if (error) throw error;
        const pageFiles = data || [];
        allRawFiles.push(...pageFiles);
        // 如果本页数据少于 PAGE_SIZE，说明没有更多了
        hasMore = pageFiles.length === PAGE_SIZE;
        offset += PAGE_SIZE;
      }

      const files = allRawFiles.map(file => {
        const category = getCategoryByAssetId(file.name);
        return {
          id: file.id || file.name, name: file.name, path: `assets/${file.name}`,
          size: file.metadata?.size || 0, type: file.metadata?.mimetype || 'application/octet-stream',
          url: supabase.storage.from('scratch-assets').getPublicUrl(`assets/${file.name}`).data.publicUrl,
          uploadedAt: file.created_at || new Date().toISOString(), category,
        };
      });
      setUploadedFiles(files);
    } catch (error) {
      
    } finally {
      setIsLoading(false);
    }
  }, [assetMapping]);

  useEffect(() => { loadUploadedFiles(); }, [loadUploadedFiles]);

  // JSON 上传处理
  const handleJsonUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const jsonFiles = files.filter(f => f.name.endsWith('.json'));
    if (jsonFiles.length === 0) { alert('请选择 JSON 文件'); return; }

    const newMapping: AssetMapping = {};
    let totalLoaded = 0;
    const categoryStats = { sprites: 0, costumes: 0, backdrops: 0, sounds: 0 };

    for (const file of jsonFiles) {
      try {
        const text = await file.text();
        const data = JSON.parse(text);

        // 更精确的分类判断
        const fileNameLower = file.name.toLowerCase();
        let category: 'sprites' | 'costumes' | 'backdrops' | 'sounds' = 'sprites';

        // 优先匹配明确的文件名
        if (fileNameLower.includes('costumes')) category = 'costumes';
        else if (fileNameLower.includes('backdrops')) category = 'backdrops';
        else if (fileNameLower.includes('sounds')) category = 'sounds';
        else if (fileNameLower.includes('sprites')) category = 'sprites';
        // 备选匹配
        else if (fileNameLower.includes('costume')) category = 'costumes';
        else if (fileNameLower.includes('backdrop')) category = 'backdrops';
        else if (fileNameLower.includes('sound')) category = 'sounds';

        

        let items: any[] = [];
        if (Array.isArray(data)) { items = data; }
        else if (data && typeof data === 'object') {
          // 处理可能的嵌套结构
          const values = Object.values(data);
          // 如果值是数组，展开它
          if (values.length > 0 && Array.isArray(values[0])) {
            items = values.flat();
          } else {
            items = values;
          }
        }

        

        items.forEach((item: any) => {
          if (!item || typeof item !== 'object') return;

          // 尝试多种可能的字段名
          const assetId = item.assetId || item.asset_id || item.id || item.md5 || item.hash || item.md5ext;
          const name = item.name || item.title || item.filename || item.fileName || assetId;

          if (assetId) {
            const assetIdStr = String(assetId);
            newMapping[assetIdStr] = { name: String(name || assetIdStr), category };
            totalLoaded++;
            categoryStats[category]++;
          }
        });
      } catch (err) {
        
        alert(`解析 ${file.name} 失败: ${err instanceof Error ? err.message : '未知错误'}`);
      }
    }

    setAssetMapping(newMapping);
    setJsonUploaded(totalLoaded > 0);

    const statsMsg = `角色: ${categoryStats.sprites}, 造型: ${categoryStats.costumes}, 背景: ${categoryStats.backdrops}, 声音: ${categoryStats.sounds}`;
    alert(`成功加载 ${totalLoaded} 个资源映射\n${statsMsg}`);

    // 重新加载文件列表以应用新映射
    await loadUploadedFiles();

    if (jsonInputRef.current) jsonInputRef.current.value = '';

    // 自动执行分类整理
    if (totalLoaded > 0) {
      await organizeAssets(newMapping);
    }
  };

  // ========== 分类整理功能（前端直接调用 Storage API） ==========

  // 从 Storage 下载 JSON 文件并构建映射
  const buildMappingFromStorage = async (): Promise<AssetMapping> => {
    const newMapping: AssetMapping = {};
    const categories = ['sprites', 'costumes', 'backdrops', 'sounds'];

    for (const category of categories) {
      const fileName = `${category}.json`;
      try {
        const { data, error } = await supabase.storage
          .from('scratch-assets')
          .download(`assets/${fileName}`);

        if (error || !data) {
          
          continue;
        }

        const text = await data.text();
        const json = JSON.parse(text);

        // 输出 JSON 的顶层结构
        
        if (!Array.isArray(json) && typeof json === 'object') {
          
        }

        let items: any[] = [];
        if (Array.isArray(json)) {
          items = json;
        } else if (json && typeof json === 'object') {
          const vals = Object.values(json);
          if (vals.length > 0 && Array.isArray(vals[0])) {
            items = vals.flat();
          } else {
            items = vals;
          }
        }

        

        // 输出前3个条目的完整结构
        for (let idx = 0; idx < Math.min(3, items.length); idx++) {
          const item = items[idx];
          if (!item || typeof item !== 'object') continue;
          
          
        }

        for (const item of items) {
          if (!item || typeof item !== 'object') continue;
          const assetId = item.assetId || item.asset_id || item.id || item.md5 || item.hash || item.md5ext;
          const name = item.name || item.title || item.filename || item.fileName || assetId;
          if (assetId) {
            const cleanAssetId = String(assetId).replace(/\.[^/.]+$/, '');
            newMapping[cleanAssetId] = { name: String(name || cleanAssetId), category: category as any };
          }
        }
      } catch (e) {
        
      }
    }

    
    // 输出前5个映射 key 和对应的文件名格式
    const sampleKeys = Object.keys(newMapping).slice(0, 5);
    
    return newMapping;
  };

  const organizeAssets = async (mapping: AssetMapping) => {
    if (isOrganizing) return;
    setIsOrganizing(true);
    setOrganizeProgress({ current: 0, total: 0, message: '准备分类整理...' });
    setOrganizeResult(null);

    try {
      // 如果没有传入映射，从已加载的 assetMapping 使用
      let useMapping = Object.keys(mapping).length > 0 ? mapping : assetMapping;

      // 如果本地也没有映射，自动从 Storage 下载 JSON 文件构建
      if (Object.keys(useMapping).length === 0) {
        setOrganizeProgress({ current: 0, total: 0, message: '从云端下载 JSON 索引文件...' });
        useMapping = await buildMappingFromStorage();
        if (Object.keys(useMapping).length > 0) {
          setAssetMapping(useMapping);
          setJsonUploaded(true);
        }
      }

      if (Object.keys(useMapping).length === 0) {
        throw new Error('无法构建资源映射，请先上传 JSON 索引文件');
      }

      // Step 1: 分页获取 assets/ 目录下所有文件
      setOrganizeProgress({ current: 0, total: 0, message: '获取文件列表...' });
      const allFiles: any[] = [];
      const PAGE_SIZE = 1000;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase.storage.from('scratch-assets').list('assets', {
          limit: PAGE_SIZE,
          offset,
          sortBy: { column: 'name', order: 'asc' },
        });
        if (error) throw new Error('获取文件列表失败: ' + error.message);
        const page = data || [];
        allFiles.push(...page);
        hasMore = page.length === PAGE_SIZE;
        offset += PAGE_SIZE;
      }

      // 过滤掉 JSON 索引文件
      const assetFiles = allFiles.filter(f => !f.name.endsWith('.json'));
      const total = assetFiles.length;

      setOrganizeProgress({ current: 0, total, message: `发现 ${total} 个资源文件，开始分类...` });

      // Step 2: 逐个移动文件到对应分类目录
      let moved = 0;
      let skipped = 0;
      let failed = 0;
      const errors: string[] = [];

      // 调试：输出前3个文件名提取的 assetId 和映射中的匹配情况
      for (let debugIdx = 0; debugIdx < Math.min(3, assetFiles.length); debugIdx++) {
        const debugFile = assetFiles[debugIdx];
        const debugAssetId = debugFile.name.replace(/\.[^/.]+$/, '');
        const debugInfo = useMapping[debugAssetId];
        
      }

      for (let i = 0; i < assetFiles.length; i++) {
        const file = assetFiles[i];
        const fileName = file.name;
        const assetId = fileName.replace(/\.[^/.]+$/, '');
        const info = useMapping[assetId];

        setOrganizeProgress({ current: i + 1, total, message: `分类 ${fileName}...` });

        if (!info) {
          skipped++;
          continue;
        }

        const category = info.category;
        if (!['sprites', 'costumes', 'backdrops', 'sounds'].includes(category)) {
          skipped++;
          continue;
        }

        const oldPath = `assets/${fileName}`;
        const newPath = `${category}/${fileName}`;

        const { error: moveError } = await supabase.storage
          .from('scratch-assets')
          .move(oldPath, newPath);

        if (moveError) {
          failed++;
          errors.push(`移动 ${fileName} 失败: ${moveError.message}`);
        } else {
          moved++;
        }
      }

      setOrganizeResult({ moved, skipped, failed });

      const msg = `分类完成！\n成功移动: ${moved} 个\n跳过: ${skipped} 个\n失败: ${failed} 个`;
      alert(msg);

      // 刷新文件列表
      await loadUploadedFiles();
    } catch (error) {
      
      alert(`分类整理失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setIsOrganizing(false);
      setOrganizeProgress({ current: 0, total: 0, message: '' });
    }
  };

  // ========== 智能上传队列 ==========

  // 上传单个文件到 Supabase Storage
  const uploadSingleFile = async (file: File, category: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64 = (e.target?.result as string).split(',')[1];
          const arrayBuffer = decode(base64);
          const filePath = `assets/${file.name}`;
          const { error } = await supabase.storage.from('scratch-assets').upload(filePath, arrayBuffer, {
            contentType: file.type, upsert: true,
          });
          if (error) throw error;
          resolve();
        } catch (err) { reject(err); }
      };
      reader.onerror = () => reject(new Error('文件读取失败'));
      reader.readAsDataURL(file);
    });
  };

  // 启动上传队列
  const startUploadQueue = async (files: File[]) => {
    if (queueState.isRunning) return;

    queueAbortRef.current = false;
    const totalBatches = Math.ceil(files.length / BATCH_SIZE);
    const categoryMap = jsonUploaded ? assetMapping : {};

    // 初始化队列项
    const initialItems: QueueItem[] = files.map(file => {
      const assetId = file.name.replace(/\.[^/.]+$/, '');
      const category = categoryMap[assetId]?.category || 'sprites';
      return {
        id: generateId(), file, status: 'pending', progress: 0,
        message: '等待上传', category, retryCount: 0,
      };
    });

    setQueueItems(initialItems);
    setQueueState({
      isRunning: true, totalFiles: files.length, successCount: 0, failCount: 0,
      currentBatch: 0, totalBatches, isPaused: false, isComplete: false,
    });

    let successCount = 0;
    let failCount = 0;

    // 分批串行上传
    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      if (queueAbortRef.current) break;

      // 检查暂停状态，等待恢复
      while (queueState.isPaused && !queueAbortRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      if (queueAbortRef.current) break;

      const start = batchIndex * BATCH_SIZE;
      const end = Math.min(start + BATCH_SIZE, files.length);
      const batchItems = initialItems.slice(start, end);

      setQueueState(prev => ({ ...prev, currentBatch: batchIndex + 1 }));

      // 串行上传本批次
      for (let i = 0; i < batchItems.length; i++) {
        if (queueAbortRef.current) break;

        const itemIndex = start + i;
        const item = batchItems[i];
        let uploaded = false;

        // 重试机制
        for (let retry = 0; retry <= MAX_RETRIES && !queueAbortRef.current; retry++) {
          const currentStatus = retry > 0 ? 'retrying' : 'uploading';
          setQueueItems(prev => prev.map((qi, idx) =>
            idx === itemIndex ? { ...qi, status: currentStatus, progress: retry > 0 ? 0 : 50, message: retry > 0 ? `第 ${retry} 次重试...` : '正在上传...', retryCount: retry } : qi
          ));

          try {
            await uploadSingleFile(item.file, item.category);
            uploaded = true;
            successCount++;
            setQueueItems(prev => prev.map((qi, idx) =>
              idx === itemIndex ? { ...qi, status: 'success', progress: 100, message: '上传成功' } : qi
            ));
            setQueueState(prev => ({ ...prev, successCount }));
            break;
          } catch (err) {
            const errorMsg = err instanceof Error ? err.message : '上传失败';
            if (retry < MAX_RETRIES) {
              // 等待后重试
              await new Promise(resolve => setTimeout(resolve, 2000 * (retry + 1)));
            } else {
              failCount++;
              setQueueItems(prev => prev.map((qi, idx) =>
                idx === itemIndex ? { ...qi, status: 'error', progress: 0, message: `失败: ${errorMsg}`, error: errorMsg, retryCount: MAX_RETRIES } : qi
              ));
              setQueueState(prev => ({ ...prev, failCount }));
            }
          }
        }
      }

      // 批次间延迟
      if (batchIndex < totalBatches - 1 && !queueAbortRef.current) {
        await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
      }
    }

    // 队列完成
    setQueueState(prev => ({ ...prev, isRunning: false, isComplete: true }));
    await loadUploadedFiles();

    // 上传报告
    const reportMsg = `上传完成！\n成功: ${successCount} 个\n失败: ${failCount} 个\n总计: ${files.length} 个`;
    alert(reportMsg);
  };

  // 暂停/恢复上传
  const togglePause = () => {
    setQueueState(prev => ({ ...prev, isPaused: !prev.isPaused }));
  };

  // 取消上传
  const cancelUpload = () => {
    queueAbortRef.current = true;
    setQueueState(prev => ({ ...prev, isRunning: false, isPaused: false }));
    setQueueItems(prev => prev.map(qi => qi.status === 'pending' || qi.status === 'uploading' || qi.status === 'retrying' ? { ...qi, status: 'error', message: '已取消' } : qi));
  };

  // 选择文件后启动队列
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    startUploadQueue(files);
  };

  // ========== 删除功能 ==========

  const handleDelete = async (fileName: string) => {
    if (!confirm(`确定要删除文件 "${fileName}" 吗？`)) return;
    try {
      const { error } = await supabase.storage.from('scratch-assets').remove([`assets/${fileName}`]);
      if (error) throw error;
      setUploadedFiles(prev => prev.filter(f => f.name !== fileName));
      setSelectedFiles(prev => { const s = new Set(prev); s.delete(`assets/${fileName}`); return s; });
    } catch (error) {
      
      alert('删除文件失败，请重试');
    }
  };

  const toggleFileSelection = (fileKey: string) => {
    setSelectedFiles(prev => { const s = new Set(prev); s.has(fileKey) ? s.delete(fileKey) : s.add(fileKey); return s; });
  };

  const toggleAllSelection = () => {
    if (selectedFiles.size === uploadedFiles.length) { setSelectedFiles(new Set()); }
    else { setSelectedFiles(new Set(uploadedFiles.map(f => `assets/${f.name}`))); }
  };

  const handleBatchDelete = async () => {
    if (selectedFiles.size === 0) return;
    if (!confirm(`确定要删除选中的 ${selectedFiles.size} 个文件吗？此操作不可恢复。`)) return;
    setIsBatchDeleting(true);
    let successCount = 0, failCount = 0;
    try {
      for (const key of selectedFiles) {
        const name = key.replace('assets/', '');
        try {
          const { error } = await supabase.storage.from('scratch-assets').remove([`assets/${name}`]);
          if (error) throw error;
          successCount++;
        } catch (err) { failCount++; }
      }
      await loadUploadedFiles();
      setSelectedFiles(new Set());
      alert(failCount > 0 ? `批量删除完成：成功 ${successCount} 个，失败 ${failCount} 个` : `成功删除 ${successCount} 个文件`);
    } catch (error) { alert('批量删除失败，请重试'); }
    finally { setIsBatchDeleting(false); }
  };

  // ========== 同步到本地 ==========

  const handleSyncToLocal = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    setSyncProgress({ current: 0, total: uploadedFiles.length, message: '准备同步...' });
    try {
      const total = uploadedFiles.length;
      for (let i = 0; i < total; i++) {
        const file = uploadedFiles[i];
        setSyncProgress({ current: i + 1, total, message: `正在下载 ${file.name}...` });
        try {
          const response = await fetch(file.url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `${file.category}/${file.name}`;
          document.body.appendChild(a); a.click();
          document.body.removeChild(a); window.URL.revokeObjectURL(url);
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err) {  }
      }
      alert(`同步完成！共 ${total} 个文件`);
    } catch (error) { alert('同步失败，请重试'); }
    finally { setIsSyncing(false); setSyncProgress({ current: 0, total: 0, message: '' }); }
  };

  const categoryCounts = ASSET_CATEGORIES.map(cat => ({ ...cat, count: uploadedFiles.filter(f => f.category === cat.id).length }));

  // ========== 渲染 ==========

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scratch 资源库管理</h1>
          <p className="text-gray-500 mt-1">上传和管理 Scratch 编辑器的角色、声音、背景和造型资源</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSyncToLocal} disabled={isLoading || isSyncing || uploadedFiles.length === 0} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Download className={`w-4 h-4 ${isSyncing ? 'animate-bounce' : ''}`} />
            {isSyncing ? `同步中 ${syncProgress.current}/${syncProgress.total}` : '同步到本地'}
          </button>
          <button onClick={loadUploadedFiles} disabled={isLoading} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            刷新列表
          </button>
        </div>
      </div>

      {/* 同步进度 */}
      {isSyncing && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-blue-900">正在同步到本地...</span>
                <span className="text-sm text-blue-700">{syncProgress.current} / {syncProgress.total}</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${syncProgress.total > 0 ? (syncProgress.current / syncProgress.total) * 100 : 0}%` }} />
              </div>
              <p className="text-xs text-blue-600 mt-1">{syncProgress.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* JSON 上传区域 */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileJson className="w-5 h-5 text-blue-600" />
              JSON 索引文件
            </h3>
            <p className="text-sm text-gray-500 mt-1">请先上传 4 个 JSON 文件（sprites.json、costumes.json、backdrops.json、sounds.json）</p>
          </div>
          <div className="flex items-center gap-2">
            {jsonUploaded && <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">已加载 {Object.keys(assetMapping).length} 个映射</span>}
            <input ref={jsonInputRef} type="file" multiple accept=".json" onChange={handleJsonUpload} className="hidden" />
            <button onClick={() => jsonInputRef.current?.click()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">选择 JSON 文件</button>
            <button
              onClick={() => organizeAssets({})}
              disabled={isOrganizing}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {isOrganizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {isOrganizing ? '分类中...' : '自动分类'}
            </button>
          </div>
        </div>
        {jsonUploaded && (
          <div className="bg-green-50 rounded-lg p-3 border border-green-200">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <p className="text-sm text-green-800">JSON 映射已加载，上传资源文件时会根据 assetId 自动分类</p>
            </div>
          </div>
        )}
        {/* 分类整理进度 */}
        {isOrganizing && (
          <div className="mt-4 bg-purple-50 rounded-lg p-3 border border-purple-200">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
              <p className="text-sm text-purple-800">{organizeProgress.message || '正在分类整理...'}</p>
            </div>
          </div>
        )}
        {/* 分类结果 */}
        {organizeResult && !isOrganizing && (
          <div className="mt-4 bg-green-50 rounded-lg p-3 border border-green-200">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <p className="text-sm text-green-800">
                分类完成！成功: {organizeResult.moved} | 跳过: {organizeResult.skipped} | 失败: {organizeResult.failed}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 分类统计 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {categoryCounts.map(category => (
          <div key={category.id} className="relative p-4 rounded-xl border border-gray-200 bg-white">
            <div className={`w-10 h-10 rounded-lg ${category.bgColor} flex items-center justify-center mb-3`}><category.icon className={`w-5 h-5 ${category.color}`} /></div>
            <div className="text-left"><div className="font-semibold text-gray-900">{category.label}</div><div className="text-xs text-gray-500 mt-1">{category.count} 个文件</div></div>
          </div>
        ))}
      </div>

      {/* 上传区域 */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={(e) => { e.preventDefault(); setIsDragging(false); const files = Array.from(e.dataTransfer.files); if (files.length > 0) startUploadQueue(files); }}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white hover:border-gray-400'} ${queueState.isRunning ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} className="hidden" />
        <div className="flex flex-col items-center gap-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${isDragging ? 'bg-blue-100' : 'bg-gray-100'}`}>
            <Upload className={`w-8 h-8 ${isDragging ? 'text-blue-600' : 'text-gray-400'}`} />
          </div>
          <div>
            <p className="text-lg font-medium text-gray-900">{isDragging ? '释放文件以上传' : '拖拽文件到此处或点击选择'}</p>
            <p className="text-sm text-gray-500 mt-1">
              支持一次性选择全部文件（2000+），系统会自动分批上传。
              {jsonUploaded ? ' 已加载 JSON 映射，将自动分类' : ' 未加载 JSON 映射，文件默认放入 sprites'}
            </p>
          </div>
          <button onClick={() => fileInputRef.current?.click()} disabled={queueState.isRunning} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50">选择文件</button>
        </div>
      </div>

      {/* ========== 上传队列面板 ========== */}
      <AnimatePresence>
        {(queueState.isRunning || queueState.isComplete) && queueItems.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* 队列头部：整体进度 + 控制按钮 */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {queueState.isRunning && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
                  {queueState.isComplete && queueState.failCount === 0 && <CheckCircle className="w-4 h-4 text-green-500" />}
                  {queueState.isComplete && queueState.failCount > 0 && <AlertCircle className="w-4 h-4 text-yellow-500" />}
                  <span className="font-medium text-gray-900">
                    {queueState.isComplete ? '上传完成' : queueState.isPaused ? '已暂停' : '正在上传...'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {queueState.isRunning && (
                    <>
                      <button onClick={togglePause} className="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors">
                        {queueState.isPaused ? '继续' : '暂停'}
                      </button>
                      <button onClick={cancelUpload} className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors">取消</button>
                    </>
                  )}
                  {queueState.isComplete && (
                    <button onClick={() => { setQueueItems([]); setQueueState({ isRunning: false, totalFiles: 0, successCount: 0, failCount: 0, currentBatch: 0, totalBatches: 0, isPaused: false, isComplete: false }); }} className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">关闭</button>
                  )}
                </div>
              </div>

              {/* 进度条 */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div className="h-3 rounded-full transition-all duration-300 flex">
                  <div className="bg-green-500 rounded-l-full" style={{ width: `${(queueState.successCount / queueState.totalFiles) * 100}%` }} />
                  <div className="bg-red-500" style={{ width: `${(queueState.failCount / queueState.totalFiles) * 100}%` }} />
                </div>
              </div>

              {/* 统计数字 */}
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-600">批次 {queueState.currentBatch}/{queueState.totalBatches}</span>
                <span className="text-green-600 font-medium">{queueState.successCount} 成功</span>
                <span className="text-red-600 font-medium">{queueState.failCount} 失败</span>
                <span className="text-gray-500">{queueState.totalFiles - queueState.successCount - queueState.failCount} 待上传</span>
              </div>
            </div>

            {/* 队列详情列表（只显示最近活动和失败项） */}
            <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
              {/* 失败项优先显示 */}
              {queueItems.filter(qi => qi.status === 'error').map(qi => (
                <div key={qi.id} className="px-4 py-2 flex items-center gap-3 bg-red-50">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-red-900 truncate">{qi.file.name}</div>
                    <div className="text-xs text-red-600">{qi.message} (重试 {qi.retryCount} 次)</div>
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-red-100 rounded text-red-700">{ASSET_CATEGORIES.find(c => c.id === qi.category)?.label || qi.category}</span>
                </div>
              ))}
              {/* 正在上传的项 */}
              {queueItems.filter(qi => qi.status === 'uploading' || qi.status === 'retrying').map(qi => (
                <div key={qi.id} className="px-4 py-2 flex items-center gap-3 bg-blue-50">
                  <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-blue-900 truncate">{qi.file.name}</div>
                    <div className="text-xs text-blue-600">{qi.message}</div>
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-blue-100 rounded text-blue-700">{ASSET_CATEGORIES.find(c => c.id === qi.category)?.label || qi.category}</span>
                </div>
              ))}
              {/* 最近成功的5项 */}
              {queueItems.filter(qi => qi.status === 'success').slice(-5).map(qi => (
                <div key={qi.id} className="px-4 py-2 flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-700 truncate">{qi.file.name}</div>
                  </div>
                  <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">{ASSET_CATEGORIES.find(c => c.id === qi.category)?.label || qi.category}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========== 已上传文件列表 ========== */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-gray-700">全部文件 - 已上传</span>
            <span className="text-sm text-gray-500">({uploadedFiles.length})</span>
          </div>
          {selectedFiles.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">已选择 {selectedFiles.size} 个文件</span>
              <button onClick={handleBatchDelete} disabled={isBatchDeleting} className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 text-sm">
                {isBatchDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                批量删除
              </button>
            </div>
          )}
        </div>
        {isLoading ? (
          <div className="p-12 text-center"><Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto" /><p className="text-gray-500 mt-2">加载中...</p></div>
        ) : uploadedFiles.length === 0 ? (
          <div className="p-12 text-center"><File className="w-12 h-12 text-gray-300 mx-auto" /><p className="text-gray-500 mt-2">暂无文件</p></div>
        ) : (
          <div className="divide-y divide-gray-100">
            <div className="px-4 py-2 bg-gray-50 flex items-center gap-3">
              <input type="checkbox" checked={selectedFiles.size === uploadedFiles.length && uploadedFiles.length > 0} onChange={toggleAllSelection} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-xs font-medium text-gray-500">全选</span>
            </div>
            {uploadedFiles.map(file => {
              const categoryInfo = ASSET_CATEGORIES.find(c => c.id === file.category);
              const fileKey = `assets/${file.name}`;
              const isSelected = selectedFiles.has(fileKey);
              return (
                <div key={file.name} className={`px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggleFileSelection(fileKey)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${file.type.startsWith('image/') ? 'bg-purple-50' : file.type.startsWith('audio/') ? 'bg-blue-50' : 'bg-gray-50'}`}>
                    {file.type.startsWith('image/') ? <ImageIcon className="w-5 h-5 text-purple-600" /> : file.type.startsWith('audio/') ? <Music className="w-5 h-5 text-blue-600" /> : <File className="w-5 h-5 text-gray-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{file.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                      <span>{formatFileSize(file.size)}</span>
                      <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">{categoryInfo?.label || file.category}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={file.url} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><ExternalLink className="w-4 h-4" /></a>
                    <button onClick={() => handleDelete(file.name)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}